import type { MobileColumnFilter } from '@/shared/mobile-service/api/cors-mobile-api';
import { gqlEnum, isGraphQLName } from './gql-literal';

/**
 * Per-column filter UI state. Each shape maps to a Prisma-style comparator
 * object accepted by mobile-service ``where`` inputs (see
 * ``graphql/shared/filters/comparators.py``).
 */
export type StringFilterOp = 'contains' | 'equals' | 'startsWith' | 'endsWith';
export type NumberFilterOp = 'equals' | 'gt' | 'gte' | 'lt' | 'lte';

export type ColumnFilterValue =
  | { kind: 'string'; op: StringFilterOp; value: string }
  | { kind: 'number'; op: NumberFilterOp; value: string }
  | { kind: 'boolean'; value: boolean }
  | { kind: 'enum'; values: string[] }
  | { kind: 'datetime'; gte?: string; lte?: string };

export type FilterState = Record<string, ColumnFilterValue>;

/** Default (empty) filter value for a column's filter kind. */
export function emptyFilter(kind: MobileColumnFilter): ColumnFilterValue {
  switch (kind) {
    case 'string':
      return { kind: 'string', op: 'contains', value: '' };
    case 'number':
      return { kind: 'number', op: 'equals', value: '' };
    case 'boolean':
      return { kind: 'boolean', value: true };
    case 'enum':
      return { kind: 'enum', values: [] };
    case 'datetime':
      return { kind: 'datetime' };
  }
}

/** True when a filter value carries a meaningful constraint. */
export function isFilterActive(value: ColumnFilterValue | undefined): boolean {
  if (!value) return false;
  switch (value.kind) {
    case 'string':
      return value.value.trim() !== '';
    case 'number':
      return value.value.trim() !== '' && Number.isFinite(Number(value.value));
    case 'boolean':
      return true;
    case 'enum':
      return value.values.length > 0;
    case 'datetime':
      return Boolean(value.gte || value.lte);
  }
}

/**
 * ``datetime-local`` is the browser's wall clock with no offset. Parse it as
 * local time and emit a real UTC instant. A value that already carries ``Z``
 * or an offset is left on that instant.
 */
export function normalizeDateTime(raw: string): string | null {
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
}

/** Compile a single column filter into its GraphQL comparator object. */
function compileComparator(value: ColumnFilterValue): Record<string, unknown> | null {
  switch (value.kind) {
    case 'string': {
      const v = value.value.trim();
      if (v === '') return null;
      return { [value.op]: v };
    }
    case 'number': {
      const n = Number(value.value);
      if (!Number.isFinite(n)) return null;
      return { [value.op]: n };
    }
    case 'boolean':
      return { equals: value.value };
    case 'enum':
      return value.values.length > 0 ? { in: value.values } : null;
    case 'datetime': {
      const out: Record<string, unknown> = {};
      if (value.gte) {
        const gte = normalizeDateTime(value.gte);
        if (gte) out.gte = gte;
      }
      if (value.lte) {
        const lte = normalizeDateTime(value.lte);
        if (lte) out.lte = lte;
      }
      return Object.keys(out).length > 0 ? out : null;
    }
  }
}

/**
 * Field names ``compileWhere`` may emit.
 *
 * ``undefined`` means the artifact published no ``filterable_fields`` (name
 * check only). A set, including an empty one, is an allowlist: URL keys that
 * are not in it are dropped.
 */
export function filterableFieldAllowlist(
  filterable: Record<string, string> | null | undefined,
): ReadonlySet<string> | undefined {
  if (!filterable) return undefined;
  return new Set(Object.keys(filterable).filter(isGraphQLName));
}

/**
 * Compile the active column filters into a Prisma-style ``where`` object,
 * combining multiple columns with implicit ``AND`` (top-level keys are ANDed by
 * the backend). Returns ``null`` when no filters are active.
 *
 * Keys come from the URL, so each one must be a GraphQL name and, when
 * ``allowedFields`` is set, a member of ``capabilities.filterable_fields``.
 */
export function compileWhere(
  filters: FilterState,
  allowedFields?: ReadonlySet<string>,
): Record<string, unknown> | null {
  const where: Record<string, unknown> = {};
  for (const [field, value] of Object.entries(filters)) {
    if (!isGraphQLName(field)) continue;
    if (allowedFields && !allowedFields.has(field)) continue;
    if (!isFilterActive(value)) continue;
    const comparator = compileComparator(value);
    if (comparator) where[field] = comparator;
  }
  return Object.keys(where).length > 0 ? where : null;
}

/** Build the ``orderBy`` argument object (direction as a GraphQL enum token). */
export function buildOrderBy(
  sort: { field: string; direction: 'asc' | 'desc' } | null,
): Record<string, unknown> | null {
  if (!sort) return null;
  return { field: sort.field, direction: gqlEnum(sort.direction === 'asc' ? 'ASC' : 'DESC') };
}

/**
 * ``orderBy`` for a view field, but only when the table config has already
 * vouched that the server will accept it.
 *
 * List queries raise a GraphQL error for unknown sort fields rather than
 * ignoring them (``orderBy.field 'name' is not sortable; allowed: …``). A card
 * or timeline view that sorted on its title would take down the whole page on
 * every type whose name isn't in that allow-list. The table view's
 * ``sortable: true`` / ``default_sort`` is the one signal we have that a field
 * has survived that check in production.
 */
export function orderByIfSortable(
  field: string | undefined | null,
  direction: 'asc' | 'desc',
  table: { default_sort?: { field: string } | null; column_overrides?: { field: string; sortable?: boolean }[] } | undefined,
): Record<string, unknown> | null {
  if (!field || field.includes('.')) return null;
  const override = table?.column_overrides?.find((column) => column.field === field);
  const allowed = override?.sortable === true || table?.default_sort?.field === field;
  if (!allowed) return null;
  return buildOrderBy({ field, direction });
}
